// Enhanced Promotion Management Component - Consistent with Report Design
// Supports: Refer-a-Friend, Early Signup Bonus, Driver Signup Bonus, Top 10 Rewards

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import MinimalLayout from './DashboardLayout/MinimalLayout';
import { 
  Settings, 
  Users, 
  Car, 
  Trophy, 
  Gift, 
  DollarSign, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle,
  Edit,
  Save,
  RefreshCw,
  Info,
  Target,
  Award,
  Star,
  Zap,
  Users2,
  Crown,
  Medal,
  ChevronUp,
  ChevronDown,
  Calendar,
  Activity
} from 'lucide-react';

const EnhancedPromotionManagement = () => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProcessingTop10, setIsProcessingTop10] = useState(false);
  const [analytics, setAnalytics] = useState([]);
  const [currentLeaderboard, setCurrentLeaderboard] = useState({ drivers: [], passengers: [] });
  const [activeTab, setActiveTab] = useState('configurations');
  const [showGuides, setShowGuides] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    configurations: true,
    analytics: true,
    leaderboard: true
  });
  const [timeFilter, setTimeFilter] = useState('all');
  const [promoWallet, setPromoWallet] = useState({
    totalTransferred: 0,
    totalSpent: 0,
    currentBalance: 0
  });

  // Wallet funding state
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [isFundingWallet, setIsFundingWallet] = useState(false);
  const [walletForm, setWalletForm] = useState({
    amount: '',
    source: 'external', // external, revenue
    allocationMethod: 'equal', // equal, custom, percentage
    allocations: []
  });
  const [promotionBudgets, setPromotionBudgets] = useState({});
  const [showBudgetTracking, setShowBudgetTracking] = useState(true);

  // Form state for editing configurations
  const [editForm, setEditForm] = useState({
    rewardAmount: '',
    isActive: true
  });

  // Fetch promotion budgets from database
  const fetchPromoBudgets = async () => {
    try {
      console.log('📊 Fetching promotion budgets from database...');
      const response = await axios.get(`${API_URL}/promotions/wallet/budgets`);
      
      if (response.data && response.data.status === 'success') {
        const budgets = response.data.data;
        const budgetMap = {};
        
        budgets.forEach(budget => {
          const key = `${budget.promo_id}-${budget.config_type}`;
          budgetMap[key] = {
            promoId: budget.promo_id,
            configType: budget.config_type,
            promotionName: budget.promotion_name,
            totalAllocated: budget.total_allocated,
            spent: budget.spent,
            remaining: budget.remaining,
            status: budget.status,
            utilizationPercentage: budget.utilization_percentage,
            lastAllocatedAt: budget.last_allocated_at,
            lastSpentAt: budget.last_spent_at
          };
        });
        
        setPromotionBudgets(budgetMap);
        console.log('✅ Promotion budgets fetched:', Object.keys(budgetMap).length, 'budgets');
      }
    } catch (error) {
      console.error('❌ Error fetching promotion budgets:', error);
    }
  };

  // Get promotion type details
  const getPromotionTypeInfo = (configType) => {
    const typeInfo = {
      'refer_friend': {
        name: 'Refer-a-Friend Program',
        description: 'Reward users who refer new customers to the platform',
        icon: <Users className="text-blue-600" />,
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-600',
        tips: ['Both referrer and referred user get rewards', 'Automatic payout after first ride', 'Track referral success in analytics']
      },
      'early_signup_customer': {
        name: 'Early Customer Signup Bonus',
        description: 'Reward passengers (customers) who sign up early for both private rides and rideshare services - separate from driver signup',
        icon: <Gift className="text-green-600" />,
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-600',
        tips: ['One-time bonus for early passenger adopters', 'Helps drive initial customer acquisition for private rides/rideshare', 'Separate promotion from driver signup bonuses']
      },
      'early_signup_driver': {
        name: 'Early Driver Signup Bonus',
        description: 'Reward drivers who join early during platform launch - separate from customer signup',
        icon: <Car className="text-yellow-600" />,
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-600',
        tips: ['Incentivizes driver onboarding', 'Builds initial driver base', 'Separate promotion from customer signup bonuses']
      },
      'top_drivers': {
        name: 'Top 10 Drivers Monthly',
        description: 'Reward the top 10 performing drivers each month',
        icon: <Trophy className="text-red-600" />,
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-600',
        tips: ['Based on monthly earnings', 'Automatically calculated', 'Motivates driver performance']
      },
      'top_passengers': {
        name: 'Top 10 Passengers Monthly',
        description: 'Reward the top 10 most active passengers each month',
        icon: <Star className="text-purple-600" />,
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-600',
        tips: ['Based on monthly trip count', 'Encourages platform usage', 'Builds customer loyalty']
      }
    };
    return typeInfo[configType] || { name: configType, description: '', icon: <Settings className="text-gray-600" />, bgColor: 'bg-gray-50', borderColor: 'border-gray-200', textColor: 'text-gray-600', tips: [] };
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    fetchConfigurations();
    fetchAnalytics();
    fetchLeaderboard();
    fetchPromoWallet();
    fetchPromoBudgets();
  }, []);

  const fetchConfigurations = async () => {
    try {
      console.log('Fetching configurations from:', `${API_URL}/promotions/configurations`);
      setLoading(true);
      const response = await axios.get(`${API_URL}/promotions/configurations`);
      
      console.log('Configurations response:', response.data);
      
      // Extract the actual array from the backend response structure
      const responseData = response.data;
      const configurationsArray = responseData && responseData.data ? responseData.data : [];
      
      console.log('Extracted configurations array:', configurationsArray);
      
      // Ensure we always set an array
      const newConfigurations = Array.isArray(configurationsArray) ? configurationsArray : [];
      setConfigurations(newConfigurations);
      
      console.log('Set configurations state:', newConfigurations);
      
    } catch (error) {
      console.error('Error fetching configurations:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      // Don't set empty array, let demo data show
      console.log('Keeping existing configurations due to fetch error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/promotions/analytics/enhanced`);
      // Ensure we always set an array, even if response.data is not an array
      setAnalytics(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Set empty array to prevent reduce errors
      setAnalytics([]);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const [driverResponse, passengerResponse] = await Promise.all([
        axios.get(`${API_URL}/promotions/leaderboard/current/driver`),
        axios.get(`${API_URL}/promotions/leaderboard/current/passenger`)
      ]);
      
      setCurrentLeaderboard({
        drivers: driverResponse.data || [],
        passengers: passengerResponse.data || []
      });
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      // Set empty arrays to prevent errors
      setCurrentLeaderboard({ drivers: [], passengers: [] });
    }
  };

  const fetchPromoWallet = async () => {
    try {
      console.log('Fetching promo wallet data...');
      
      // Use actual API call
      const response = await axios.get(`${API_URL}/promotions/wallet/summary`);
      
      if (response.data && response.data.status === 'success') {
        const walletData = response.data.data;
        setPromoWallet({
          totalTransferred: walletData.total_transferred || 0,
          totalSpent: walletData.total_spent || 0,
          currentBalance: walletData.current_balance || 0
        });
        console.log('✅ Wallet data fetched from API:', walletData);
      } else {
        // Fallback to zeros if API returns no data
        setPromoWallet({
          totalTransferred: 0,
          totalSpent: 0,
          currentBalance: 0
        });
        console.log('⚠️ API returned no data, using zeros');
      }
      
    } catch (error) {
      console.error('❌ Error fetching promo wallet data:', error);
      // Set default values on error
      setPromoWallet({
        totalTransferred: 0,
        totalSpent: 0,
        currentBalance: 0
      });
    }
  };

  // Wallet funding functions
  const handleOpenWalletModal = () => {
    const activeConfigs = getDisplayConfigurations().filter(c => c.is_active);
    
    // Include all active promotions (regardless of current balance)
    const availableConfigs = activeConfigs;
    
    const initialAllocations = availableConfigs.map(config => ({
      promoId: config.promo_id,
      configType: config.config_type,
      name: getPromotionTypeInfo(config.config_type).name,
      amount: 0,
      percentage: 0
    }));
    
    setWalletForm({
      amount: '',
      source: 'external',
      allocationMethod: 'equal',
      allocations: initialAllocations
    });
    setIsWalletModalOpen(true);
  };

  const calculateAllocations = (totalAmount, method, allocations) => {
    if (!totalAmount || !allocations.length) return allocations;
    
    const amount = parseFloat(totalAmount);
    const updatedAllocations = [...allocations];
    
    switch (method) {
      case 'equal':
        const equalAmount = amount / allocations.length;
        return updatedAllocations.map(alloc => ({
          ...alloc,
          amount: equalAmount,
          percentage: (equalAmount / amount) * 100
        }));
        
      case 'percentage':
        return updatedAllocations.map(alloc => ({
          ...alloc,
          amount: (alloc.percentage / 100) * amount
        }));
        
      case 'custom':
        // Keep custom amounts as set by user
        return updatedAllocations;
        
      default:
        return allocations;
    }
  };

  const handleAllocationMethodChange = (method) => {
    const currentAmount = walletForm.amount;
    const newAllocations = calculateAllocations(currentAmount, method, walletForm.allocations);
    
    setWalletForm({
      ...walletForm,
      allocationMethod: method,
      allocations: newAllocations
    });
  };

  const handleAmountChange = (amount) => {
    const newAllocations = calculateAllocations(amount, walletForm.allocationMethod, walletForm.allocations);
    
    setWalletForm({
      ...walletForm,
      amount,
      allocations: newAllocations
    });
  };

  const handleCustomAllocationChange = (index, field, value) => {
    const newAllocations = [...walletForm.allocations];
    newAllocations[index] = { ...newAllocations[index], [field]: value };
    
    // If percentage changed, recalculate amount
    if (field === 'percentage') {
      const percentage = parseFloat(value) || 0;
      newAllocations[index].amount = (percentage / 100) * parseFloat(walletForm.amount || 0);
    }
    
    setWalletForm({
      ...walletForm,
      allocations: newAllocations
    });
  };

  const handleFundWallet = async () => {
    try {
      console.log('Funding wallet:', walletForm);
      
      // Validate form
      if (!walletForm.amount || parseFloat(walletForm.amount) <= 0) {
        alert('Please enter a valid amount to fund the wallet.');
        return;
      }
      
      if (walletForm.allocations.length === 0) {
        alert('Please allocate funds to at least one promotion.');
        return;
      }
      
      // Validate allocations sum
      const totalAllocatedAmount = walletForm.allocations.reduce((sum, alloc) => sum + parseFloat(alloc.amount || 0), 0);
      if (Math.abs(totalAllocatedAmount - parseFloat(walletForm.amount)) > 0.01) {
        alert('Allocated amounts must equal the total funding amount.');
        return;
      }
      
      // Set loading state
      setIsFundingWallet(true);
      
      // Use actual API call
      const response = await axios.post(`${API_URL}/promotions/wallet/fund`, {
        amount: parseFloat(walletForm.amount),
        source: walletForm.source,
        allocations: walletForm.allocations
      });
      
      if (response.data && response.data.status === 'success') {
        console.log('✅ Wallet funding API response:', response.data);
        
        // Update wallet state with API response
        const newBalance = promoWallet.currentBalance + parseFloat(walletForm.amount);
        setPromoWallet({
          ...promoWallet,
          totalTransferred: promoWallet.totalTransferred + parseFloat(walletForm.amount),
          currentBalance: newBalance
        });
        
        // Update promotion budgets with real data
        const newBudgets = { ...promotionBudgets };
        walletForm.allocations.forEach(alloc => {
          const key = `${alloc.promoId}-${alloc.configType}`;
          newBudgets[key] = {
            totalAllocated: (newBudgets[key]?.totalAllocated || 0) + alloc.amount,
            remaining: (newBudgets[key]?.totalAllocated || 0) + alloc.amount,
            spent: (newBudgets[key]?.spent || 0)
          };
        });
        setPromotionBudgets(newBudgets);
        
        // Update wallet to show real aggregated data
        const totalAllocatedSum = Object.values(newBudgets).reduce((sum, budget) => sum + budget.totalAllocated, 0);
        const totalSpent = Object.values(newBudgets).reduce((sum, budget) => sum + budget.spent, 0);
        const currentBalance = totalAllocatedSum - totalSpent;
        
        setPromoWallet({
          totalTransferred: promoWallet.totalTransferred + parseFloat(walletForm.amount),
          totalSpent: totalSpent,
          currentBalance: currentBalance
        });
        
        // Show success message from API
        const successMessage = response.data.message || `Wallet funded successfully! $${parseFloat(walletForm.amount).toFixed(2)} allocated to ${walletForm.allocations.length} promotion(s).`;
        alert(successMessage);
        
        // Close modal and reset form
        setIsWalletModalOpen(false);
        setWalletForm({
          amount: '',
          source: 'external',
          allocationMethod: 'equal',
          allocations: []
        });
        
        // Refresh wallet data to show updated state
        await fetchPromoWallet();
        await fetchPromoBudgets();
        
        console.log('✅ Wallet funding completed successfully');
      } else {
        throw new Error(response.data?.message || 'Failed to fund wallet');
      }
      
    } catch (error) {
      console.error('❌ Error funding wallet:', error);
      
      // Extract user-friendly message from backend response
      let errorMessage = 'Please try again.';
      
      if (error.response && error.response.data) {
        // Use the backend's custom message
        errorMessage = error.response.data.message || error.response.data.details || errorMessage;
      } else if (error.message) {
        // Fallback to error message if no response data
        errorMessage = error.message;
      }
      
      alert(`Error funding wallet: ${errorMessage}`);
    } finally {
      // Reset loading state
      setIsFundingWallet(false);
    }
  };

  const getPromotionBudgetInfo = (promoId, configType) => {
    const key = `${promoId}-${configType}`;
    return promotionBudgets[key] || {
      totalAllocated: 0,
      remaining: 0,
      spent: 0
    };
  };

  const handleEditConfig = (config) => {
    setSelectedConfig(config);
    setEditForm({
      rewardAmount: config.reward_amount || '',
      isActive: config.is_active || false
    });
    setIsEditModalOpen(true);
  };

  const handleSaveConfig = async () => {
    try {
      console.log('Saving configuration:', {
        promoId: selectedConfig.promo_id,
        configType: selectedConfig.config_type,
        formData: editForm
      });

      // Prepare data with correct types
      const requestData = {
        rewardAmount: parseFloat(editForm.rewardAmount),
        isActive: Boolean(editForm.isActive)
      };

      console.log('Sending to backend:', requestData);

      // Update the configuration
      const response = await axios.put(
        `${API_URL}/promotions/configurations/${selectedConfig.promo_id}/${selectedConfig.config_type}`,
        requestData
      );
      
      console.log('Save response:', response.data);
      
      // Close modal first
      setIsEditModalOpen(false);
      setSelectedConfig(null);
      
      // Force refresh all data
      console.log('Refreshing data...');
      await Promise.all([
        fetchConfigurations(),
        fetchAnalytics(),
        fetchLeaderboard(),
        fetchPromoWallet()
      ]);
      
      // Show success message
      alert('Configuration updated successfully! Data has been refreshed.');
      
    } catch (error) {
      console.error('Error updating configuration:', error);
      
      // More detailed error message
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Unknown error occurred';
      
      alert(`Error updating configuration: ${errorMessage}`);
      
      // Even on error, try to refresh data to show current state
      try {
        await fetchConfigurations();
      } catch (refreshError) {
        console.error('Error refreshing after save failure:', refreshError);
      }
    }
  };

  const handleProcessTop10 = async (userType) => {
    try {
      setIsProcessingTop10(true);
      
      await axios.post(`${API_URL}/promotions/process/top10`, {
        promoId: `PROMO-TOP-${userType.toUpperCase()}-001`,
        userType: userType,
        periodStart: new Date().toISOString().split('T')[0].replace(/-/g, '-'),
        periodEnd: new Date().toISOString().split('T')[0].replace(/-/g, '-')
      });
      
      // Refresh leaderboard
      await fetchLeaderboard();
      
      alert(`Top 10 ${userType}s rewards processed successfully!`);
    } catch (error) {
      console.error('Error processing top 10 rewards:', error);
      alert('Error processing top 10 rewards');
    } finally {
      setIsProcessingTop10(false);
    }
  };

  // Get display configurations (for stats and render)
  const getDisplayConfigurations = () => {
    if (configurations.length > 0) {
      console.log('Raw configurations from backend:', configurations);
      
      // Remove duplicates based on promo_id and config_type combination
      const uniqueConfigs = configurations.reduce((acc, config) => {
        const key = `${config.promo_id}-${config.config_type}`;
        if (!acc.has(key)) {
          acc.set(key, config);
        } else {
          console.log('Duplicate found:', key, config);
        }
        return acc;
      }, new Map());
      
      const uniqueArray = Array.from(uniqueConfigs.values());
      console.log('Unique configurations:', uniqueArray);
      
      return uniqueArray;
    } else {
      return [
        {
          promo_id: 'DEMO-REF-001',
          config_type: 'refer_friend',
          reward_amount: 0.00,
          is_active: false
        },
        {
          promo_id: 'DEMO-SIGNUP-CUST-001',
          config_type: 'early_signup_customer',
          reward_amount: 0.00,
          is_active: false
        },
        {
          promo_id: 'DEMO-SIGNUP-DRV-001',
          config_type: 'early_signup_driver',
          reward_amount: 0.00,
          is_active: false
        },
        {
          promo_id: 'DEMO-TOP-DRV-001',
          config_type: 'top_drivers',
          reward_amount: 0.00,
          is_active: false
        },
        {
          promo_id: 'DEMO-TOP-PAX-001',
          config_type: 'top_passengers',
          reward_amount: 0.00,
          is_active: false
        }
      ];
    }
  };

  // Safe stats calculations
  const getStats = () => {
    const safeAnalytics = Array.isArray(analytics) ? analytics : [];
    const safeConfigs = getDisplayConfigurations();
    
    return {
      activePromotions: configurations.length > 0 ? safeConfigs?.filter(c => c.is_active).length || 0 : 0,
      totalRewards: configurations.length > 0 ? safeAnalytics.reduce((sum, item) => sum + (item.total_rewards || 0), 0) : 0,
      uniqueUsers: configurations.length > 0 ? safeAnalytics.reduce((sum, item) => sum + (item.unique_users || 0), 0) : 0,
      totalAmount: configurations.length > 0 ? safeAnalytics.reduce((sum, item) => sum + (item.total_amount || 0), 0) : 0
    };
  };

  if (loading) {
    return (
      <MinimalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </MinimalLayout>
    );
  }

  return (
    <MinimalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
            <Award className="mr-3 text-blue-600" />
            Enhanced Promotion Management
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage and monitor all promotional campaigns in one place
          </p>
        </div>

        {/* Promo Wallet Summary */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-700 dark:to-blue-700 rounded-lg shadow-md p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <DollarSign className="mr-2" size={24} />
              Promotion Wallet Summary
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleOpenWalletModal}
                className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center"
              >
                <DollarSign className="mr-2" size={16} />
                Fund Wallet
              </button>
              <button
                onClick={() => fetchPromoWallet()}
                className="text-white hover:text-gray-200 transition-colors"
                title="Refresh wallet data"
              >
                <RefreshCw size={20} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                ${(promoWallet?.totalTransferred || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-purple-100 text-sm">Total Transferred</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                ${(promoWallet?.totalSpent || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-purple-100 text-sm">Total Spent</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                ${(promoWallet?.currentBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <p className="text-purple-100 text-sm">Current Balance</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-purple-400/30">
            <div className="flex items-center justify-between text-sm">
              <span className="text-purple-100">Wallet Utilization</span>
              <span className="font-medium">
                {(promoWallet?.totalTransferred || 0) > 0 
                  ? ((promoWallet?.totalSpent || 0) / (promoWallet?.totalTransferred || 0) * 100).toFixed(1)
                  : 0}% spent
              </span>
            </div>
            <div className="mt-2 w-full bg-purple-400/30 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(promoWallet?.totalTransferred || 0) > 0 
                    ? ((promoWallet?.totalSpent || 0) / (promoWallet?.totalTransferred || 0)) * 100 
                    : 0}%` 
                }}
              />
            </div>
          </div>
        </div>

        {/* Budget Tracking Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <TrendingUp className="mr-2 text-green-600" />
              Individual Promotion Budget Tracking
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowBudgetTracking(!showBudgetTracking)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showBudgetTracking ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>
          </div>
          
          {showBudgetTracking && (
            <div className="space-y-4">
              {Object.entries(promotionBudgets).length > 0 ? (
                Object.entries(promotionBudgets).map(([key, budget]) => {
                  const [promoId, configType] = key.split('-');
                  const typeInfo = getPromotionTypeInfo(configType);
                  const spentPercentage = budget.totalAllocated > 0 ? (budget.spent / budget.totalAllocated) * 100 : 0;
                  const remainingPercentage = budget.totalAllocated > 0 ? (budget.remaining / budget.totalAllocated) * 100 : 0;
                  
                  return (
                    <div key={key} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          {typeInfo.icon}
                          <div className="ml-3">
                            <h3 className="font-semibold text-gray-900 dark:text-white">
                              {budget.promotionName || typeInfo.name}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {promoId}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          budget.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {budget.status === 'active' ? 'Active' : 'Deactivated'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Allocated</p>
                          <p className="text-lg font-bold text-blue-600">
                            ${budget.totalAllocated.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Spent</p>
                          <p className="text-lg font-bold text-red-600">
                            ${budget.spent.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Remaining</p>
                          <p className={`text-lg font-bold ${
                            budget.status === 'active' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            ${budget.remaining.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Utilization</p>
                          <p className="text-lg font-bold text-purple-600">
                            {spentPercentage.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                          <span>Budget Utilization</span>
                          <span>{spentPercentage.toFixed(1)}% spent</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-red-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span>${budget.spent.toFixed(2)} spent</span>
                          <span>${budget.remaining.toFixed(2)} remaining</span>
                        </div>
                      </div>
                      
                      {/* Alert if budget is low */}
                      {budget.remaining > 0 && budget.remaining < budget.totalAllocated * 0.2 && (
                        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center">
                            <AlertCircle className="mr-2" size={16} />
                            Budget running low ({remainingPercentage.toFixed(1)}% remaining)
                          </p>
                        </div>
                      )}
                      
                      {/* Alert if budget is completely empty */}
                      {budget.status === 'depleted' && (
                        <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center">
                            <AlertCircle className="mr-2" size={16} />
                            Promotion deactivated - No remaining budget
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-4">
                    <DollarSign size={48} className="mx-auto" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    No promotion budgets allocated yet
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    Fund the wallet and allocate budgets to see tracking information
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Time Filter */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex items-center space-x-2 mb-2 sm:mb-0">
              <Calendar className="text-blue-600" size={20} />
              <span className="font-medium text-gray-700 dark:text-gray-300">Time Period:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "all", label: "All Time" },
                { value: "today", label: "Today" },
                { value: "7days", label: "Past 7 Days" },
                { value: "14days", label: "Past 14 Days" },
                { value: "30days", label: "Past 30 Days" }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setTimeFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    timeFilter === filter.value
                      ? "bg-blue-600 text-white shadow-lg transform scale-105"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Promotions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getStats().activePromotions}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Target className="text-blue-600" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Rewards Given</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getStats().totalRewards}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <DollarSign className="text-green-600" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Users</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{getStats().uniqueUsers}</p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-full">
                <Users2 className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Amount</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">${getStats().totalAmount}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Demo Mode Warning */}
        {configurations.length === 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-1 mr-3" size={20} />
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-3">⚠️ DEMO MODE - No Backend Data</h3>
                <div className="text-sm text-yellow-800 dark:text-yellow-200 space-y-2">
                  <p><strong>This is demonstration data only.</strong> The application is not connected to the backend database.</p>
                  <p>• All names are fake (John Doe)</p>
                  <p>• All amounts are zeros</p>
                  <p>• All configurations are inactive</p>
                  <p>To see real data, ensure the backend is running and the database is populated.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Guide Section */}
        {showGuides && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-start">
              <Info className="text-blue-600 dark:text-blue-400 mt-1 mr-3" size={20} />
              <div>
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">🚀 Quick Guide to Enhanced Promotions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800 dark:text-blue-200">
                  <div>
                    <p className="font-medium mb-2">📋 Configurations:</p>
                    <p className="mb-3">Set up reward amounts and activation status for each promotion type.</p>
                    <p className="font-medium mb-2">📊 Analytics:</p>
                    <p className="mb-3">Monitor performance metrics like rewards given, unique users, and total amounts.</p>
                  </div>
                  <div>
                    <p className="font-medium mb-2">🏆 Leaderboard:</p>
                    <p className="mb-3">View top performers and process monthly rewards for drivers and passengers.</p>
                    <p className="font-medium mb-2">💡 Tips:</p>
                    <p>Regularly review analytics to optimize promotion performance and ROI.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configurations Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <Settings className="mr-2 text-blue-600" />
                Promotion Configurations
              </h2>
              <div className="ml-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  configurations.length > 0 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {configurations.length > 0 ? 'Live Data' : 'Demo Mode'}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowGuides(!showGuides)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <Info size={20} />
              </button>
              <button
                onClick={() => {
                  fetchConfigurations();
                  fetchAnalytics();
                  fetchLeaderboard();
                  fetchPromoWallet();
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <RefreshCw size={20} />
              </button>
              <button
                onClick={() => toggleSection('configurations')}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {expandedSections.configurations ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              <button
                onClick={() => {
                  console.log('Current configurations state:', configurations);
                  console.log('Current analytics state:', analytics);
                  console.log('Current leaderboard state:', currentLeaderboard);
                  alert('Debug info logged to console. Check browser dev tools.');
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                title="Debug: Log current state to console"
              >
                <Activity size={20} />
              </button>
            </div>
          </div>
          
          {expandedSections.configurations && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {getDisplayConfigurations().map((config, index) => {
                const typeInfo = getPromotionTypeInfo(config.config_type);
                const uniqueKey = `${config.promo_id}-${config.config_type}`;
                return (
                  <div key={uniqueKey} className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className={`p-4 rounded-lg ${typeInfo.bgColor} ${typeInfo.borderColor} border mb-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          {typeInfo.icon}
                          <h3 className="ml-2 font-semibold text-gray-900">{config.promo_id}</h3>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          config.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {config.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-center mb-4">
                      <div className="text-3xl font-bold text-gray-900 mb-1">
                        ${config.reward_amount.toFixed(2)}
                      </div>
                      <p className="text-sm text-gray-600">Reward Amount</p>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-1">{typeInfo.name}</h4>
                      <p className="text-sm text-gray-600">{typeInfo.description}</p>
                    </div>

                    {typeInfo.tips.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">💡 Key Benefits</h5>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {typeInfo.tips.map((tip, tipIndex) => (
                            <li key={tipIndex}>• {tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Budget Tracking */}
                    {config.is_active && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">📊 Budget Tracking</h5>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Allocated:</span>
                            <div className="font-bold text-blue-600 dark:text-blue-400">
                              ${getPromotionBudgetInfo(config.promo_id, config.config_type).totalAllocated.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Remaining:</span>
                            <div className={`font-bold ${
                              getPromotionBudgetInfo(config.promo_id, config.config_type).remaining > 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              ${getPromotionBudgetInfo(config.promo_id, config.config_type).remaining.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        {getPromotionBudgetInfo(config.promo_id, config.config_type).totalAllocated > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                              <div 
                                className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ 
                                  width: `${Math.max(0, (getPromotionBudgetInfo(config.promo_id, config.config_type).remaining / getPromotionBudgetInfo(config.promo_id, config.config_type).totalAllocated) * 100)}%` 
                                }}
                              />
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {((getPromotionBudgetInfo(config.promo_id, config.config_type).remaining / getPromotionBudgetInfo(config.promo_id, config.config_type).totalAllocated) * 100).toFixed(1)}% remaining
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => handleEditConfig(config)}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <Edit size={16} className="mr-2" />
                      Edit Configuration
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Analytics Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Activity className="mr-2 text-green-600" />
              Promotion Analytics
            </h2>
            <button
              onClick={() => toggleSection('analytics')}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {expandedSections.analytics ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.analytics && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Promotion Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total Rewards
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Unique Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(analytics.length > 0 ? analytics : [
                    { promotion_type: 'Refer-a-Friend', total_rewards: 0, unique_users: 0, total_amount: 0 },
                    { promotion_type: 'Early Signup - Customer', total_rewards: 0, unique_users: 0, total_amount: 0 },
                    { promotion_type: 'Early Signup - Driver', total_rewards: 0, unique_users: 0, total_amount: 0 },
                    { promotion_type: 'Top 10 Drivers', total_rewards: 0, unique_users: 0, total_amount: 0 },
                    { promotion_type: 'Top 10 Passengers', total_rewards: 0, unique_users: 0, total_amount: 0 }
                  ]).map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {item.promotion_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {item.total_rewards}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {item.unique_users}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        ${item.total_amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          active
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Leaderboard Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Trophy className="mr-2 text-yellow-600" />
              Performance Leaderboard
            </h2>
            <button
              onClick={() => toggleSection('leaderboard')}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {expandedSections.leaderboard ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
          
          {expandedSections.leaderboard && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Drivers */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Crown className="mr-2 text-yellow-600" />
                  Top Drivers
                </h3>
                <div className="space-y-2">
                  {(currentLeaderboard.drivers.length > 0 ? currentLeaderboard.drivers : [
                    { driver_name: 'John Doe', earnings: 0 },
                    { driver_name: 'John Doe', earnings: 0 },
                    { driver_name: 'John Doe', earnings: 0 },
                    { driver_name: 'John Doe', earnings: 0 },
                    { driver_name: 'John Doe', earnings: 0 }
                  ]).map((driver, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        <span className="text-lg font-bold text-gray-500 dark:text-gray-400 mr-3">#{index + 1}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{driver.driver_name}</span>
                      </div>
                      <span className="text-sm font-bold text-green-600">${driver.earnings}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleProcessTop10('driver')}
                  disabled={isProcessingTop10}
                  className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isProcessingTop10 ? 'Processing...' : 'Process Rewards'}
                </button>
              </div>

              {/* Top Passengers */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Star className="mr-2 text-purple-600" />
                  Top Passengers
                </h3>
                <div className="space-y-2">
                  {(currentLeaderboard.passengers.length > 0 ? currentLeaderboard.passengers : [
                    { passenger_name: 'John Doe', trip_count: 0 },
                    { passenger_name: 'John Doe', trip_count: 0 },
                    { passenger_name: 'John Doe', trip_count: 0 },
                    { passenger_name: 'John Doe', trip_count: 0 },
                    { passenger_name: 'John Doe', trip_count: 0 }
                  ]).map((passenger, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center">
                        <span className="text-lg font-bold text-gray-500 dark:text-gray-400 mr-3">#{index + 1}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{passenger.passenger_name}</span>
                      </div>
                      <span className="text-sm font-bold text-purple-600">{passenger.trip_count} trips</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => handleProcessTop10('passenger')}
                  disabled={isProcessingTop10}
                  className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isProcessingTop10 ? 'Processing...' : 'Process Rewards'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <Edit className="mr-2" />
                Edit Configuration
              </h3>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ×
              </button>
            </div>
            
            {selectedConfig && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Promotion ID
                  </label>
                  <input
                    type="text"
                    value={selectedConfig.promo_id}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Configuration Type
                  </label>
                  <input
                    type="text"
                    value={selectedConfig.config_type}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reward Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.rewardAmount}
                    onChange={(e) => setEditForm({...editForm, rewardAmount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveConfig}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
                  >
                    <Save size={16} className="mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wallet Funding Modal */}
      {isWalletModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <DollarSign className="mr-2" />
                Fund Promotion Wallet
              </h3>
              <button
                onClick={() => setIsWalletModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount to Add to Wallet
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={walletForm.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter amount"
                />
              </div>

              {/* Funding Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Funding Source
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600">
                    <input
                      type="radio"
                      value="external"
                      checked={walletForm.source === 'external'}
                      onChange={(e) => setWalletForm({...walletForm, source: e.target.value})}
                      className="mr-2"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">External/Cash</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Fund from outside the system</div>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-600">
                    <input
                      type="radio"
                      value="revenue"
                      checked={walletForm.source === 'revenue'}
                      onChange={(e) => setWalletForm({...walletForm, source: e.target.value})}
                      className="mr-2"
                    />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Revenue Wallet</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Transfer from revenue wallet (ID: 1)</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Allocation Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Allocation Method
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="equal"
                      checked={walletForm.allocationMethod === 'equal'}
                      onChange={(e) => handleAllocationMethodChange(e.target.value)}
                      className="mr-2"
                    />
                    Equal Split
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="percentage"
                      checked={walletForm.allocationMethod === 'percentage'}
                      onChange={(e) => handleAllocationMethodChange(e.target.value)}
                      className="mr-2"
                    />
                    By Percentage
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="custom"
                      checked={walletForm.allocationMethod === 'custom'}
                      onChange={(e) => handleAllocationMethodChange(e.target.value)}
                      className="mr-2"
                    />
                    Custom Amounts
                  </label>
                </div>
              </div>

              {/* Allocation Breakdown */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Budget Allocation for Active Promotions
                </h4>
                
                <div className="space-y-3">
                    {walletForm.allocations.map((allocation, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {allocation.name}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {allocation.promoId}
                        </span>
                      </div>
                      
                      {walletForm.allocationMethod === 'equal' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Amount
                            </label>
                            <div className="text-lg font-bold text-blue-600">
                              ${allocation.amount.toFixed(2)}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Percentage
                            </label>
                            <div className="text-lg font-bold text-green-600">
                              {allocation.percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      ) : walletForm.allocationMethod === 'percentage' ? (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Percentage
                            </label>
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              max="100"
                              value={allocation.percentage}
                              onChange={(e) => handleCustomAllocationChange(index, 'percentage', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Amount
                            </label>
                            <div className="text-lg font-bold text-blue-600">
                              ${allocation.amount.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Custom Amount
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={allocation.amount}
                              onChange={(e) => handleCustomAllocationChange(index, 'amount', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                              Percentage
                            </label>
                            <div className="text-lg font-bold text-green-600">
                              {walletForm.amount ? ((allocation.amount / parseFloat(walletForm.amount)) * 100).toFixed(1) : 0}%
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Total Allocation:
                  </span>
                  <span className="text-xl font-bold text-blue-600">
                    ${walletForm.allocations.reduce((sum, alloc) => sum + alloc.amount, 0).toFixed(2)}
                  </span>
                </div>
                {walletForm.amount && (
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Remaining:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      ${(parseFloat(walletForm.amount) - walletForm.allocations.reduce((sum, alloc) => sum + alloc.amount, 0)).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setIsWalletModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFundWallet}
                  disabled={!walletForm.amount || walletForm.allocations.length === 0 || isFundingWallet}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isFundingWallet ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Funding...
                    </>
                  ) : (
                    <>
                      <DollarSign size={16} className="mr-2" />
                      Fund Wallet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MinimalLayout>
  );
};

export default EnhancedPromotionManagement;
